import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Users, Plus, Copy, Check, Pencil, Power, Link as LinkIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PARTNER_TYPE_LABELS = {
  mortgage_broker: "Mortgage Broker",
  sales_agent: "Sales Agent",
  accountant: "Accountant",
  financial_planner: "Financial Planner",
  other: "Other",
};
const partnerTypeLabel = (t) => PARTNER_TYPE_LABELS[t] || "Other";

const emptyForm = {
  name: "",
  company: "",
  email: "",
  partner_type: "mortgage_broker",
  referral_code: "",
  benefit_type: "price",
  researcher_discount_percent: 0,
  trial_days: 30,
};

export default function MyPartners() {
  const [partners, setPartners] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const refLink = (code) => `${window.location.origin}/register?ref=${code}`;

  const fetchPartners = async () => {
    try {
      const res = await api.get("/team/partners");
      setPartners(res.data.partners || []);
      setIsAdmin(!!res.data.is_admin);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load your partners.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      company: p.company || "",
      email: p.email || "",
      partner_type: p.partner_type || "other",
      referral_code: p.referral_code || "",
      benefit_type: p.benefit_type || "price",
      researcher_discount_percent: p.researcher_discount_percent || 0,
      trial_days: p.trial_days || 30,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Partner name is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/team/partners/${editing.partner_id}`, form);
        toast.success("Partner updated.");
      } else {
        await api.post("/team/partners", form);
        toast.success("Partner created — share their link to start referring.");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
      fetchPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save partner.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (p) => {
    try {
      await api.post(`/team/partners/${p.partner_id}/toggle`);
      fetchPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update partner.");
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const totalReferred = partners.reduce((acc, p) => acc + (p.referred_count || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-partners-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-[#547d91]" />
            My Referral Partners
          </h2>
          <p className="text-slate-500 mt-1">
            {isAdmin
              ? "All referral partners (admin view)."
              : "Create referral partners, share their link, and track clients you've referred."}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#547d91] hover:bg-[#456a7a]" onClick={openCreate} data-testid="add-partner-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Partner" : "Add New Partner"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Partner name *</Label>
                <Input id="name" data-testid="partner-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="ABC Mortgage Brokers" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="partner_type">Partner type</Label>
                <select id="partner_type" data-testid="partner-type-select" value={form.partner_type}
                  onChange={(e) => setForm({ ...form, partner_type: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="mortgage_broker">Mortgage Broker</option>
                  <option value="sales_agent">Sales Agent</option>
                  <option value="accountant">Accountant</option>
                  <option value="financial_planner">Financial Planner</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="partner@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="referral_code">Referral code</Label>
                <Input id="referral_code" data-testid="partner-referral-code-input" value={form.referral_code}
                  onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                  placeholder="Leave blank to auto-generate" maxLength={20} />
                <p className="text-xs text-slate-500">3–20 letters/numbers, must be unique. Leave blank to auto-generate.</p>
              </div>
              <div className="space-y-2 border-t pt-4">
                <Label htmlFor="benefit_type">Referral benefit</Label>
                <select id="benefit_type" data-testid="partner-benefit-type" value={form.benefit_type}
                  onChange={(e) => setForm({ ...form, benefit_type: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="price">Price discount (%)</option>
                  <option value="trial">Extended free trial (days)</option>
                </select>
                {form.benefit_type === "price" ? (
                  <div className="space-y-1 pt-1">
                    <Label htmlFor="discount">Discount (%)</Label>
                    <Input id="discount" data-testid="partner-discount-percent" type="number" min={0} max={100}
                      value={form.researcher_discount_percent}
                      onChange={(e) => { const v = Math.max(0, Math.min(100, parseInt(e.target.value || "0", 10))); setForm({ ...form, researcher_discount_percent: isNaN(v) ? 0 : v }); }}
                      className="w-32" />
                    <p className="text-xs text-slate-500">Discount off the AI Buyers Agent subscription for this partner's clients. 100 = free access.</p>
                  </div>
                ) : (
                  <div className="space-y-1 pt-1">
                    <Label htmlFor="trial_days">Free trial length (days)</Label>
                    <Input id="trial_days" data-testid="partner-trial-days" type="number" min={1} max={365}
                      value={form.trial_days}
                      onChange={(e) => { const v = Math.max(1, Math.min(365, parseInt(e.target.value || "0", 10))); setForm({ ...form, trial_days: isNaN(v) ? 1 : v }); }}
                      className="w-32" />
                    <p className="text-xs text-slate-500">Referred clients get an extended free trial (standard is 7 days).</p>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full bg-[#547d91] hover:bg-[#456a7a]" disabled={saving} data-testid="partner-submit-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (editing ? "Save Changes" : "Create Partner")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#547d91]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="active-partner-count">{partners.filter(p => p.is_active).length}</p>
              <p className="text-sm text-slate-500">Active Partners</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" data-testid="total-referred-count">{totalReferred}</p>
              <p className="text-sm text-slate-500">Clients Referred</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partner list */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Partners</CardTitle>
        </CardHeader>
        <CardContent>
          {partners.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No partners yet</p>
              <p className="text-sm text-slate-400 mt-1">Click "Add Partner" to create your first referral partner.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="partners-list">
              {partners.map((p) => (
                <div key={p.partner_id} className="border border-slate-200 rounded-xl p-4" data-testid={`partner-card-${p.referral_code}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      {p.company && <p className="text-sm text-slate-500">{p.company}</p>}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs text-slate-600 border-slate-200">{partnerTypeLabel(p.partner_type)}</Badge>
                        <Badge variant={p.is_active ? "default" : "destructive"} className="text-xs">{p.is_active ? "Active" : "Inactive"}</Badge>
                        <Badge variant="secondary" className="text-xs">{p.referred_count || 0} referred</Badge>
                        {isAdmin && p.owner_name && (
                          <Badge variant="outline" className="text-xs text-slate-500 border-slate-200">by {p.owner_name}</Badge>
                        )}
                        {p.benefit_type === "trial"
                          ? <Badge variant="secondary" className="text-xs">{p.trial_days}-day trial</Badge>
                          : (p.researcher_discount_percent > 0 && <Badge variant="secondary" className="text-xs">{p.researcher_discount_percent}% off</Badge>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} title="Edit" data-testid={`edit-partner-${p.referral_code}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggle(p)}
                        className={p.is_active ? "text-red-500 hover:text-red-700" : "text-emerald-600 hover:text-emerald-700"}
                        title={p.is_active ? "Deactivate" : "Reactivate"} data-testid={`toggle-partner-${p.referral_code}`}>
                        <Power className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <span className="text-xs text-slate-400">Code</span>
                      <code className="text-xs font-semibold tracking-wide" data-testid={`partner-code-${p.referral_code}`}>{p.referral_code}</code>
                      <Button variant="ghost" size="sm" className="ml-auto h-7 px-2" onClick={() => copy(p.referral_code, `code-${p.referral_code}`)} data-testid={`copy-code-${p.referral_code}`}>
                        {copied === `code-${p.referral_code}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <LinkIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-xs text-slate-500 truncate">{refLink(p.referral_code)}</span>
                      <Button variant="ghost" size="sm" className="ml-auto h-7 px-2" onClick={() => copy(refLink(p.referral_code), `link-${p.referral_code}`)} data-testid={`copy-link-${p.referral_code}`}>
                        {copied === `link-${p.referral_code}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
